import type { Map as MapboxMap, MapMouseEvent } from 'mapbox-gl';
import { type RefObject, useRef } from 'react';
import { formatNumber } from '@/libs/utils/format';
import { registerMapEvent } from '@/libs/utils/registerMapEvents';
import { LayerId, SourceId } from '../constants';
import { positionMapTooltip } from '../utils/positionMapTooltip';

const TOOLTIP_WIDTH = 265;
const TOOLTIP_HEIGHT = 180;

// Hover tooltip + feature-state for the location's highest-AADT segments
export function useMapInteractions(map: RefObject<MapboxMap | null>) {
  const hoveredIdRef = useRef<string | number | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  function getOrCreateTooltip(): HTMLDivElement {
    if (!tooltipRef.current) {
      const tooltip = document.createElement('div');
      tooltip.className = 'traffic-tooltip';
      tooltip.style.cssText = `
        position: absolute;
        pointer-events: none;
        z-index: 10;
        display: none;
      `;
      map.current!.getContainer().appendChild(tooltip);
      tooltipRef.current = tooltip;
    }
    return tooltipRef.current;
  }

  function handleMouseEnter() {
    map.current!.getCanvas().style.cursor = 'pointer';
  }

  function handleMouseLeave() {
    map.current!.getCanvas().style.cursor = '';
    const tooltip = tooltipRef.current;
    if (tooltip) {
      tooltip.style.display = 'none';
    }

    if (hoveredIdRef.current !== null) {
      map.current!.setFeatureState(
        { source: SourceId.MAX_AADT, id: hoveredIdRef.current },
        { hover: false },
      );
      hoveredIdRef.current = null;
    }
  }

  function handleMouseMove(event: MapMouseEvent) {
    const feature = map.current!.queryRenderedFeatures(event.point, {
      layers: [LayerId.TRAFFIC_MAX_AADT],
    })[0];
    if (!feature) {
      const tooltip = tooltipRef.current;
      if (tooltip) tooltip.style.display = 'none';
      return;
    }

    // Reset previously hovered
    if (hoveredIdRef.current !== null && hoveredIdRef.current !== feature.id) {
      map.current!.setFeatureState(
        { source: SourceId.MAX_AADT, id: hoveredIdRef.current },
        { hover: false },
      );
    }
    const geographicId = feature?.properties?.geographic_id;
    hoveredIdRef.current = geographicId;
    map.current!.setFeatureState({ source: SourceId.MAX_AADT, id: geographicId }, { hover: true });

    const road = feature?.properties?.road_name ?? '';
    const aadt = formatNumber(feature?.properties?.aadt) ?? '';
    const sourceId = feature?.properties?.source_id ?? 'Mytraffic';

    const tooltip = getOrCreateTooltip();
    // Every feature of this layer is a highest-AADT segment, so the badge is
    // unconditional.
    tooltip.innerHTML = `
      <div class="flex flex-col w-[16.563rem] text-sm bg-white mb-2 p-3 border rounded-md shadow-[0_4px_8px_0_#00000026]">
        <div class="inline-flex items-center px-3 py-1 rounded-full bg-max-aadt text-white text-xs font-medium w-fit self-start">Highest AADT</div>
        <div class="flex flex-col border-b border-primary-100 pb-2">
          <span class="text-primary-600 text-sm">Average traffic for:</span>
          <span class="font-bold">${road}</span>
        </div>
        <div class="flex justify-between border-b border-primary-100 py-2">
          <span class="text-primary-600">Avg. daily traffic:</span>
          <span class="font-bold">${aadt}</span>
        </div>
        <div class="flex justify-between py-2">
          <span class="text-primary-600">Data source:</span>
          <span class="font-bold">${sourceId}</span>
        </div>
      </div>
    `;
    tooltip.style.display = 'block';
    positionMapTooltip({
      tooltip,
      container: map.current!.getContainer(),
      point: event.point,
      size: { width: TOOLTIP_WIDTH, height: TOOLTIP_HEIGHT },
      placement: 'above-center',
    });
  }

  function setupInteractions() {
    if (!map.current) return () => {};

    const cleanupMouseEnter = registerMapEvent(
      map.current,
      'mouseenter',
      LayerId.TRAFFIC_MAX_AADT,
      handleMouseEnter,
    );
    const cleanupMouseLeave = registerMapEvent(
      map.current,
      'mouseleave',
      LayerId.TRAFFIC_MAX_AADT,
      handleMouseLeave,
    );
    const cleanupMouseMove = registerMapEvent(
      map.current,
      'mousemove',
      LayerId.TRAFFIC_MAX_AADT,
      handleMouseMove,
    );
    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
      cleanupMouseEnter();
      cleanupMouseLeave();
      cleanupMouseMove();
    };
  }

  return { setupInteractions };
}
